import dayjs from 'dayjs';
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation, matchPath } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Drawer } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { LiveBookingsHeader } from '@eatclub-apps/ec-bookings-library';
import { Box } from '@eatclubcode/ec-component-library';
import AppHeader from '../components/layout/appHeader/AppHeader';
import CustomHeader from '../components/layout/appHeader/CustomHeader';
import NavigationDrawer from '../components/layout/navigation/NavigationDrawer';
import { restaurantActivePropTypes } from '../data/models/Restaurant';
import routes from '../routes/Routes';
import { devLog } from '../utils';
import { overrideDisabledRestaurants } from '../utils/flags';
import { canEditAll, FEATURE_SCOPES, hasAccess } from '../utils/permissions';
import useStyles from './AppLayoutStyles';
import { isEmpty } from '../utils/baseHelpers';
import { getAppConfig } from '../constants/appConfig';
import { NavigationContext } from '../utils/navigationContext';

// TODO this and AppHeader have both become crazy over time with lots of interconnected logic, especially for the nav functionality and banners. Need to refactor when possible
const AppLayout = ({
  platform = '',
  goalsEnabled = true,
  obeeEnabled = false,
  premium = false,
  children,
  ecPayEnabled = false,
  restId = null,
  restaurantActive,
  restaurantsFetching = true,
  user,
}) => {
  const appConfig = getAppConfig();
  const location = useLocation();
  const classes = useStyles();
  const theme = useTheme();

  const premiumLabel = (name) => (
    <div className={classes.flex}>
      <div>{name}</div>
      <div className={classes.label}>Premium</div>
    </div>
  );

  // TODO needs a better loading state now that it's dynamic

  const getNavigationItems = () => {
    const newNavigationItems = [
      {
        id: 'insights',
        name: 'Insights',
        slug: 'insights',
        section: 1,
        allowedScopes: FEATURE_SCOPES.INSIGHTS,
        alwaysOpen: true,
        subItems: [
          {
            id: 'customer-value',
            name: 'Customer value',
            slug: 'customer-value',
            isNew: localStorage.getItem('viewedCustomerValue') !== 'true',
          },
        ],
      },
      {
        id: 'offers',
        name: 'Offers',
        slug: 'offers',
        section: 1,
      },
      {
        id: 'transactions',
        name: 'Transactions',
        slug: 'transactions',
        section: 1,
        allowedScopes: FEATURE_SCOPES.TRANSACTIONS,
      },
      {
        id: 'audience-targeting',
        name: 'Audience targeting',
        slug: 'audience-targeting',
        section: 1,
        allowedScopes: FEATURE_SCOPES.AUDIENCE_TARGETING,
      },
      {
        id: 'settlements',
        name: 'Settlements',
        slug: 'settlements',
        section: 1,
        allowedScopes: FEATURE_SCOPES.SETTLEMENTS,
      },
      {
        id: 'menus',
        name: 'Menus',
        slug: 'menus',
        section: 1,
        allowedScopes: FEATURE_SCOPES.INSIGHTS,
      },
      // TODO fix a bug where hasCompletedInitialLoad is always false for staff logins
      obeeEnabled
        ? {
            id: 'bookings',
            name: 'Bookings',
            section: 1,
            allowedScopes: FEATURE_SCOPES.BOOKINGS,
            subItems: [
              {
                id: 'runsheet',
                name: 'Run Sheet',
                slug: 'livebookings/runsheet',
              },
              {
                id: 'floorplan',
                name: premium ? 'Floor Plan' : premiumLabel('Floor Plan'),
                slug: 'livebookings/floorplan',
              },
              {
                id: 'timeline',
                name: premium ? 'Timeline' : premiumLabel('Timeline'),
                slug: 'livebookings/timeline',
              },
              {
                id: 'monthly',
                name: 'Monthly',
                slug: 'livebookings/monthly',
              },
              {
                id: 'guestbook',
                name: premium ? 'Guest Book' : premiumLabel('Guest Book'),
                slug: 'livebookings/guestbook',
              },
              {
                id: 'settings',
                name: 'Settings',
                slug: 'livebookings/settings',
              },
            ],
          }
        : {
            id: 'livebookings',
            name: 'Bookings',
            slug: 'livebookings',
            section: 1,
            allowedScopes: FEATURE_SCOPES.BOOKINGS,
          },
      // { name: 'Support', slug: 'support', section: 2 },
      {
        id: 'help-centre',
        name: 'Help centre',
        slug: 'help-centre',
        section: 2,
        allowedScopes: FEATURE_SCOPES.HELP_CENTRE,
      },
    ];

    if (platform === 'whitelabel') {
      const dealIndex = newNavigationItems.findIndex((nav) => nav.name === 'Goals');
      newNavigationItems.splice(dealIndex, 1);
    } else {
      // For restaurants which do not yet have goals enabled
      // eslint-disable-next-line no-lonely-if
      if (goalsEnabled === false) {
        const dealIndex = newNavigationItems.findIndex((nav) => nav.name === 'Offers');
        newNavigationItems[dealIndex] = {
          id: 'deals',
          name: 'Deals',
          slug: 'deals',
          section: 1,
        };
      }
    }

    const filteredNavigationItems = newNavigationItems
      // filter out blocked navigation items
      .filter((item) => !appConfig.blockedRoutes.includes(`/${item.slug}`))
      // filter out blocked sub navigation items
      .map((item) => {
        if (item.subItems) {
          const subItems = item.subItems.filter(
            (subItem) => !appConfig.blockedRoutes.includes(`/${subItem.slug}`),
          );

          return { ...item, subItems };
        }

        return { ...item };
      })
      // filter out navigation items with no slug or subItems
      .filter((item) => {
        if (!item.slug && (!Array.isArray(item.subItems) || item.subItems.length === 0)) {
          return false;
        }

        return true;
      });

    return filteredNavigationItems;
  };

  const [navigationItems, setNavigationItems] = useState(getNavigationItems());
  const [showDrawer, setShowDrawer] = useState(false); // temporary nav and tablet/mobile nav

  const contextValue = useMemo(
    () => ({ showNavigation: showDrawer, setShowNavigation: setShowDrawer }),
    [showDrawer, setShowDrawer],
  );

  const route = routes.find((routeToFind) => matchPath(location.pathname, routeToFind));

  useEffect(() => {
    setNavigationItems(getNavigationItems());
  }, [goalsEnabled, obeeEnabled, ecPayEnabled, premium]);

  // Set title and header
  useEffect(() => {
    const pageTitle = route?.pageTitle || 'Dashboard';
    const titleDelimiter = pageTitle !== '' ? ' | ' : '';
    const restaurantName = restaurantActive?.restaurant?.name;
    const titleEnd = `${import.meta.env.VITE_WEBSITE_NAME} Partners`;
    // window.document.title = `${pageTitle}${titleDelimiter}${titleEnd}`;
    window.document.title = [pageTitle, restaurantName, titleEnd]
      ?.filter((i) => !!i)
      ?.join(titleDelimiter);
  }, [location.pathname, navigationItems, restaurantActive?.restaurant?.name]);

  const handleShowDrawer = () => {
    setShowDrawer(true);
  };

  const hideDrawer = () => {
    setShowDrawer(false);
  };

  // Whether the restaurant has loaded and is considered disabled
  const restaurantIsDisabled =
    restaurantActive?.restaurant?.objectId &&
    !restaurantActive?.restaurant?.enabled &&
    !overrideDisabledRestaurants.includes(restaurantActive?.restaurant?.objectId);

  // Whether to block this route because venue is disabled (and not new)
  const routeIsDisabled = restaurantIsDisabled && !canEditAll(user) && !route?.allowIfDisabled;

  const breakpointMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const allowHideNavigation =
    !routeIsDisabled &&
    (route?.hideNavigation ||
      (route?.header === 'obee' &&
        ((route?.premiumRequired && premium) || !route?.premiumRequired))); // Whether this screen lets the side bar collapse

  // Logic for whether we are showing the navigation
  // prettier-ignore
  const isOpenDesktop =
    routeIsDisabled ? true // Never hide navigation if we're on a disabled route because we can't bring it back
    : route?.forceHideNavigation ? false // Route is special and does not allow any navigation
      : allowHideNavigation
        ? showDrawer // Defer to drawer logic if nav is hideable
        : true; // Ensure we always show the sidebar on non-obee screens

  // TODO this is a better method of checking initial load of restaurant:
  const loadingRestaurant =
    restaurantsFetching || restaurantActive.restaurantFetching || restaurantActive.fetching;

  // Force it to show if a staff account has not selected a restaurant
  const [forceShow, setForceShow] = useState(false);

  // Also force show the navigation if the user does not have permission to view the current page
  const noPermission = route?.allowedScopes && !hasAccess(user, route?.allowedScopes);

  // Ensure that if a staff user has not selected a restaurant, we show the restaurant selector no matter what
  useEffect(() => {
    if (!loadingRestaurant && (noPermission || isEmpty(restaurantActive.restaurant))) {
      setForceShow(true);
    } else {
      setForceShow(false);
    }
  }, [restaurantActive, noPermission, routeIsDisabled]);

  const isOpen = (breakpointMdDown ? showDrawer : isOpenDesktop) || forceShow; // force open if no restaurant is active for staff logins

  const renderHeader = () => {
    // If restaurant is not enabled, don't show the page (admins can still access)
    if (routeIsDisabled) {
      if (restaurantActive?.restaurant?.isNew) {
        return (
          <AppHeader showDrawer={handleShowDrawer} forceShowHeader>
            <Box>
              <p>
                Your account is being set up. Our team will contact you when your venue is ready to
                go live.
              </p>
              <br />
              <p>
                Please contact support via{' '}
                <a href={`mailto:${appConfig.supportEmail2}`} target='_blank' rel='noreferrer'>
                  {appConfig.supportEmail2}
                </a>{' '}
                if you have any questions.
              </p>
            </Box>
          </AppHeader>
        );
      }

      return (
        <AppHeader showDrawer={handleShowDrawer} forceShowHeader>
          <Box>
            This account is currently disabled. Please contact support via{' '}
            <a href={`mailto:${appConfig.supportEmail2}`} target='_blank' rel='noreferrer'>
              {appConfig.supportEmail2}
            </a>{' '}
            if you wish to have the account re-enabled.
          </Box>
        </AppHeader>
      );
    }

    if (!((route?.premiumRequired && premium) || !route?.premiumRequired)) {
      return (
        <AppHeader showDrawer={handleShowDrawer}>
          <>{children}</>
        </AppHeader>
      );
    }

    // TODO replace this with something else, but ideally don't render ec bookings until a rest ID is set
    // TODO also handle if you're staff and no restaurant is set
    // TODO can optimize further if restaurant id is stored in local state
    if (!restId) {
      return <Box />;
    }

    // Redirect if user does not have the valid permissions to view this page
    if (noPermission) {
      devLog('info', 'You cannot access this screen');

      return (
        <AppHeader showDrawer={handleShowDrawer} forceShowHeader>
          <Box>You do not have access to view this screen</Box>
        </AppHeader>
      );
    }

    switch (route?.header) {
      case 'obee':
        return (
          <LiveBookingsHeader
            showNavigation={handleShowDrawer}
            navigationOpen={isOpen}
            pageId={route?.id}
          >
            {children}
          </LiveBookingsHeader>
        );
      case 'custom':
        // Put the navigation controls in context so the custom component can use them
        return (
          <NavigationContext.Provider value={contextValue}>
            <CustomHeader showNavigation={handleShowDrawer} navigationOpen={isOpen}>
              {children}
            </CustomHeader>
          </NavigationContext.Provider>
        );
      default:
        return (
          <AppHeader showDrawer={handleShowDrawer}>
            <>{children}</>
          </AppHeader>
        );
    }
  };

  return (
    <>
      <nav className={classes.drawer}>
        <Drawer
          variant={
            !forceShow && (breakpointMdDown || allowHideNavigation) ? 'temporary' : 'persistent'
          }
          open={isOpen}
          onClose={hideDrawer}
          classes={{
            paper: classes.drawerPaper,
          }}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
            onBackdropClick: hideDrawer,
          }}
        >
          <NavigationDrawer
            hideDrawer={hideDrawer}
            navigationItems={navigationItems}
            hideNavigation={hideDrawer}
            allowHideNavigation={allowHideNavigation}
          />
        </Drawer>
      </nav>
      {restId && renderHeader()}
    </>
  );
};

AppLayout.propTypes = {
  platform: PropTypes.string,
  goalsEnabled: PropTypes.bool,
  obeeEnabled: PropTypes.bool,
  premium: PropTypes.bool,
  ecPayEnabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
  restId: PropTypes.string,
  restaurantActive: restaurantActivePropTypes.isRequired,
  restaurantsFetching: PropTypes.bool,
  user: PropTypes.shape({}).isRequired,
};

const mapStateToProps = (state) => ({
  platform: state.restaurantActive.restaurant?.platform,
  goalsEnabled: state.restaurantActive.restaurant?.offersEnabled,
  obeeEnabled: state.restaurantActive.restaurant?.obeeEnabled,
  restaurantActive: state.restaurantActive,
  restaurantsFetching: state.restaurants.fetching,
  premium: state.restaurantActive.restaurant?.premium,
  ecPayEnabled: state.restaurantActive.restaurant?.ecPayEnabled,
  restId: state.restaurantActive.restaurant.objectId,
  user: state.user,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AppLayout);
